import { useState, useRef, useEffect } from "react";
import { Surface } from "../../types/experiment";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ChevronDown, X } from "lucide-react";

interface SurfaceMultiSelectProps {
  value: Surface[];
  onChange: (value: Surface[]) => void;
}

const surfaceLabels: Record<number, string> = {};
const surfaceEntries = Object.entries(Surface)
  .filter(([k]) => isNaN(Number(k)))
  .map(([key, val]) => ({ key, val: val as number }));
surfaceEntries.forEach(({ key, val }) => { surfaceLabels[val] = key; });

export function SurfaceMultiSelect({ value, onChange }: SurfaceMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const toggle = (s: Surface) => {
    if (value.includes(s)) {
      onChange(value.filter((v) => v !== s));
    } else {
      onChange([...value, s]);
    }
  };

  const remove = (s: Surface) => {
    onChange(value.filter((v) => v !== s));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-surface2 bg-surface0 px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-mauve"
      >
        <span className={value.length === 0 ? "text-overlay1" : "text-text"}>
          {value.length === 0
            ? "Select surfaces..."
            : `${value.length} surface${value.length > 1 ? "s" : ""} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-overlay1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-surface1 bg-base p-1 shadow-lg">
          {surfaceEntries.map(({ key, val }) => (
            <label
              key={val}
              className="flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm cursor-pointer hover:bg-surface0"
            >
              <Checkbox
                checked={value.includes(val)}
                onCheckedChange={() => toggle(val)}
              />
              {key}
            </label>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {value.map((s) => (
            <Badge key={s} variant="secondary" className="text-[11px]">
              {surfaceLabels[s] ?? `Surface ${s}`}
              <button
                type="button"
                onClick={() => remove(s)}
                className="ml-1 hover:text-red transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
